import { useState } from 'react';
import type { InternetAccessAccount } from '../../api/types.js';

/**
 * Port of Android's `BlockWindowsCard` — fixed daily Sperrzeiten for one
 * child (see docs/INTERNET_ACCESS.md). Only a view onto
 * `account.blockWindows`: the backend is what refuses activation inside a
 * window and ends a running session when one begins, on its own clock, so
 * nothing here (or on the child's phone) has to be trusted to enforce it.
 */
export function BlockWindowsCard({
  account,
  saving,
  onAdd,
  onRemove,
}: {
  account: InternetAccessAccount | undefined;
  saving: boolean;
  onAdd: (startTime: string, endTime: string) => void;
  onRemove: (blockWindowId: string) => void;
}) {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const sameTime = startTime !== '' && startTime === endTime;
  const canAdd = account !== undefined && !saving && startTime !== '' && endTime !== '' && !sameTime;

  return (
    <div className="card card--inset">
      <h4 className="card__heading">Sperrzeiten (täglich)</h4>

      {account === undefined ? (
        <p className="muted">Lädt…</p>
      ) : (
        <>
          {account.activeBlockWindow && (
            <p className="balance">
              <span className="badge badge--blocked">
                Sperrzeit aktiv bis {account.activeBlockWindow.endTime} Uhr
              </span>
            </p>
          )}
          {account.blockWindows.length === 0 ? (
            <p className="muted">Keine Sperrzeiten</p>
          ) : (
            <ul className="device-list">
              {account.blockWindows.map((window) => (
                <li key={window.id} className="device">
                  <p className="device__label">
                    {window.startTime} – {window.endTime} Uhr
                    {window.startTime > window.endTime && <span className="muted"> (über Mitternacht)</span>}
                  </p>
                  <button
                    type="button"
                    className="button button--text button--danger"
                    onClick={() => onRemove(window.id)}
                    disabled={saving}
                  >
                    Entfernen
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <form
        className="block-window-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canAdd) return;
          onAdd(startTime, endTime);
          setStartTime('');
          setEndTime('');
        }}
      >
        <label className="field">
          Von
          <input
            type="time"
            className="input"
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
            required
          />
        </label>
        <label className="field">
          Bis
          <input
            type="time"
            className="input"
            value={endTime}
            onChange={(event) => setEndTime(event.target.value)}
            required
          />
        </label>
        <button type="submit" className="button button--primary" disabled={!canAdd}>
          Hinzufügen
        </button>
      </form>
      {sameTime && <p className="muted">Von und Bis dürfen nicht gleich sein — für ganztags den Schalter „Sperren“ verwenden.</p>}
    </div>
  );
}
